import React, { useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import FormatListBulletedRoundedIcon from "@mui/icons-material/FormatListBulletedRounded";
import NotesRoundedIcon from "@mui/icons-material/NotesRounded";

const sectionTitleSx = {
  fontWeight: 900,
  fontSize: "1rem",
  color: "rgba(255,255,255,0.94)",
  mb: 0.45,
};

const sectionSubSx = {
  fontSize: "0.84rem",
  color: "rgba(255,255,255,0.5)",
  mb: 2,
  lineHeight: 1.6,
};

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "16px",
    backgroundColor: "rgba(255,255,255,0.03)",
    transition: "all 0.2s ease-in-out",
    "& fieldset": { borderColor: "rgba(255,255,255,0.12)" },
    "&:hover fieldset": { borderColor: "rgba(255,255,255,0.24)" },
    "&.Mui-focused": {
      backgroundColor: "rgba(255,255,255,0.05)",
      "& fieldset": { borderColor: "rgba(0,225,255,0.55)" },
    },
  },
  "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.58)" },
  "& .MuiInputBase-input, & .MuiInputBase-inputMultiline": { color: "#fff" },
};

const blockSx = {
  p: { xs: 1.4, md: 1.8 },
  borderRadius: 2,
  border: "1px solid rgba(255,255,255,0.08)",
  bgcolor: "rgba(255,255,255,0.025)",
};

function SectionIntro({ icon, title, subtitle }) {
  return (
    <Stack spacing={1.25} sx={{ mb: 2 }}>
      <Stack direction="row" spacing={1.1} alignItems="center">
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: 2,
            display: "grid",
            placeItems: "center",
            bgcolor: "rgba(0,225,255,0.12)",
            color: "#8ef4ff",
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography sx={sectionTitleSx}>{title}</Typography>
          <Typography sx={sectionSubSx}>{subtitle}</Typography>
        </Box>
      </Stack>
    </Stack>
  );
}

export default function DescricaoStep({ state, actions, errors }) {
  const [lineInput, setLineInput] = useState("");

  const handleAddLineup = () => {
    if (!lineInput.trim()) return;
    actions.addLineup(lineInput.trim());
    setLineInput("");
  };

  return (
    <Stack spacing={2}>
      <Box sx={blockSx}>
        <SectionIntro
          icon={<DescriptionRoundedIcon sx={{ fontSize: 20 }} />}
          title="Sobre o evento"
          subtitle="Esse texto ajuda a vender a experiência. Deixe claro o clima, o público e os destaques."
        />

        <TextField
          fullWidth
          multiline
          minRows={7}
          label="Descrição completa"
          placeholder="Ex: Prepare-se para a melhor noite do ano..."
          InputLabelProps={{ shrink: true }}
          value={state.descricao.texto}
          onChange={(e) => actions.set("descricao.texto", e.target.value)}
          error={!!errors["descricao.texto"]}
          helperText={errors["descricao.texto"] || "Dica: use parágrafos curtos para facilitar a leitura na página."}
          sx={inputSx}
        />
      </Box>

      <Box sx={blockSx}>
        <SectionIntro
          icon={<FormatListBulletedRoundedIcon sx={{ fontSize: 20 }} />}
          title="Line-up / Programação"
          subtitle="Cadastre atrações, horários ou blocos da programação em uma lista clara."
        />

        <Stack direction={{ xs: "column", md: "row" }} spacing={1.2} sx={{ mb: 1.4 }}>
          <TextField
            fullWidth
            label="Novo item da programação"
            placeholder="Ex: 22h – DJ Felipe Vieira"
            value={lineInput}
            onChange={(e) => setLineInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleAddLineup();
              }
            }}
            InputLabelProps={{ shrink: true }}
            sx={inputSx}
          />
          <Button
            variant="contained"
            startIcon={<AddRoundedIcon />}
            onClick={handleAddLineup}
            disabled={!lineInput.trim()}
            sx={{
              minHeight: 54,
              px: 2,
              borderRadius: 2,
              fontWeight: 900,
              textTransform: "none",
              background:
                "linear-gradient(135deg, rgba(0,225,255,0.95), rgba(109,255,178,0.95))",
              color: "#042014",
            }}
          >
            Adicionar
          </Button>
        </Stack>

        {state.descricao.lineup.length > 0 ? (
          <Stack spacing={1}>
            {state.descricao.lineup.map((l, idx) => (
              <Paper
                key={`${l}-${idx}`}
                elevation={0}
                sx={{
                  p: 1.35,
                  borderRadius: 2,
                  bgcolor: "rgba(255,255,255,0.04)",
                  border: "1px solid rgba(255,255,255,0.08)",
                }}
              >
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                  <Stack direction="row" alignItems="center" spacing={1.2} sx={{ minWidth: 0 }}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: 2,
                        display: "grid",
                        placeItems: "center",
                        bgcolor: "rgba(0,225,255,0.12)",
                        color: "#8ef4ff",
                        flexShrink: 0,
                      }}
                    >
                      <FormatListBulletedRoundedIcon sx={{ fontSize: 18 }} />
                    </Box>
                    <Typography sx={{ color: "rgba(255,255,255,0.92)", fontWeight: 700 }}>
                      {l}
                    </Typography>
                  </Stack>
                  <IconButton
                    size="small"
                    onClick={() => actions.removeLineup(idx)}
                    sx={{ color: "rgba(255,255,255,0.46)", border: "1px solid rgba(255,255,255,0.08)" }}
                  >
                    <CloseRoundedIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Paper>
            ))}
          </Stack>
        ) : (
          <Box
            sx={{
              borderRadius: 2,
              border: "1px dashed rgba(255,255,255,0.12)",
              p: 1.5,
              textAlign: "center",
            }}
          >
            <Typography sx={{ fontSize: 13, color: "rgba(255,255,255,0.46)" }}>
              Nenhum item da programação adicionado ainda.
            </Typography>
          </Box>
        )}
      </Box>

      <Box sx={blockSx}>
        <SectionIntro
          icon={<NotesRoundedIcon sx={{ fontSize: 20 }} />}
          title="Observações finais"
          subtitle="Use este espaço para estacionamento, open bar, documentos exigidos e outras informações úteis."
        />

        <TextField
          fullWidth
          multiline
          minRows={4}
          label="Observações"
          placeholder="Ex: Estacionamento gratuito no local..."
          InputLabelProps={{ shrink: true }}
          value={state.descricao.observacoes}
          onChange={(e) => actions.set("descricao.observacoes", e.target.value)}
          sx={inputSx}
        />
      </Box>
    </Stack>
  );
}
